import React from "react";
import { ReactComponent as CalenderIcon } from "../../../../assets/icons/svg/calender-icon.svg";
import { ReactComponent as DesktopPotrateIcon } from "../../../../assets/icons/svg/desktop-potrate-icon.svg";
import { ReactComponent as TagIcon } from "../../../../assets/icons/svg/tag-icon.svg";
import { ReactComponent as WifiLiveIcon } from "../../../../assets/icons/svg/wifi-live-icon.svg";
import { ReactComponent as WifiOfflineIcon } from "../../../../assets/icons/svg/wifi-offline-icon.svg";
import { ReactComponent as Thunder } from "../../../../assets/icons/svg/thunder.svg";
import { ReactComponent as Clock } from "../../../../assets/icons/svg/clock.svg";
import { ReactComponent as SelectionIcon } from "../../../../assets/icons/svg/selection-icon.svg";
import ItemListPopup from "../../../ui/ItemListPopup";
import AnnouncementTypeCard from "../../../ui/AnnouncementTypeCard"; // Adjust path as needed

interface ScheduledCreative {
  id: string;
  title: string;
  imageUrl: string;
  orientation: "landscape" | "portrait";
  format: string;
  createdOn: string;
  dateRange: string;
  locations: string;
  tags: string[];
  isPlaying: boolean;
  scheduleType: "now" | "later" | "selection";
  announcement: "general" | "tag-level";
  scheduleDetails: {
    selectedSlots?: Set<string> | string[]; // Allow both Set and array for flexibility
    duration?: string | { label: string }; // Handle both string and object with label
    startDate?: string;
    endDate?: string;
  };
  fileFormat?: string;
  duration?: string;
  startDate?: string;
  endDate?: string;
  selectedTags?: string[];
  announcementDetails?: any;
}

interface ScheduledCreativePreviewProps {
  creative: ScheduledCreative;
  onClose: () => void;
}

const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const ScheduledCreativePreview: React.FC<ScheduledCreativePreviewProps> = ({
  creative,
  onClose,
}) => {
  /**
   * Get dimensions display text
   */
  const getDimensions = () => {
    return creative.orientation === "landscape" ? "1920 x 1080" : "1080 x 1920";
  };

  /**
   * Format the creation date for display
   */
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  /**
   * Get schedule type display text
   */
  const getScheduleTypeText = () => {
    switch (creative.scheduleType) {
      case "now":
        return "Now";
      case "later":
        return "Later";
      case "selection":
        return "Selection";
      default:
        return "Scheduled";
    }
  };

  /**
   * Get duration display text
   */
  const getDurationText = () => {
    if (creative.duration) {
      return creative.duration;
    }
    if (creative.scheduleDetails?.duration) {
      if (
        typeof creative.scheduleDetails.duration === "object" &&
        creative.scheduleDetails.duration !== null
      ) {
        return creative.scheduleDetails.duration.label || "10 sec";
      }
      return creative.scheduleDetails.duration || "10 sec";
    }
    return "10 sec";
  };

  /**
   * Get selected slots for custom schedule, handling array or Set
   */
  const getSelectedSlots = (): Set<string> => {
    if (
      creative.scheduleType === "selection" &&
      creative.scheduleDetails?.selectedSlots
    ) {
      if (Array.isArray(creative.scheduleDetails.selectedSlots)) {
        return new Set(creative.scheduleDetails.selectedSlots);
      }
      if (creative.scheduleDetails.selectedSlots instanceof Set) {
        return creative.scheduleDetails.selectedSlots;
      }
    }
    return new Set<string>();
  };

  /**
   * Get button class for calendar grid
   */
  const getButtonClass = (key: string) => {
    const slots = getSelectedSlots();
    const isSelected = slots.has(key);
    return isSelected
      ? "bg-[#CCF0FB] border border-white"
      : "bg-[#E4E4E4] opacity-30 border border-white";
  };

  /**
   * Get date range based on schedule type
   */
  const getDateRange = () => {
    if (creative.startDate && creative.endDate) {
      return `${formatDate(creative.startDate)} - ${formatDate(
        creative.endDate
      )}`;
    }

    if (creative.scheduleDetails) {
      if (creative.scheduleType === "now" && creative.scheduleDetails.endDate) {
        const startDate = formatDate(new Date().toISOString());
        const endDate = formatDate(creative.scheduleDetails.endDate);
        return `${startDate} - ${endDate}`;
      }

      if (
        creative.scheduleType === "later" &&
        creative.scheduleDetails.startDate &&
        creative.scheduleDetails.endDate
      ) {
        const startDate = formatDate(creative.scheduleDetails.startDate);
        const endDate = formatDate(creative.scheduleDetails.endDate);
        return `${startDate} - ${endDate}`;
      }
    }

    return creative.dateRange || "Date range not specified";
  };

  /**
   * Get selected locations
   */
  const getSelectedLocations = () => {
    const locations = [];
    if (creative.locations) {
      locations.push(creative.locations);
    }
    return locations.length > 0 ? locations : ["Not specified"];
  };

  /**
   * Get selected tags for display
   */
  const getSelectedTagsCount = () => {
    if (creative.tags && creative.tags.length > 0) {
      return creative.tags.length;
    }
    if (creative.announcementDetails?.selectedTags) {
      return creative.announcementDetails.selectedTags.length;
    }
    return 0;
  };

  /**
   * Get tags for display
   */
  const getTagsForDisplay = () => {
    if (creative.tags && creative.tags.length > 0) {
      return creative.tags;
    }
    if (creative.announcementDetails?.selectedTags) {
      return creative.announcementDetails.selectedTags;
    }
    return [];
  };

  const tagsToDisplay = getTagsForDisplay();

  // Get unique selected hours across all days and sort in ascending order
  const selectedSlots = getSelectedSlots();
  const selectedHours = Array.from(
    new Set(Array.from(selectedSlots).map((slot: string) => slot.split("-")[1]))
  )
    .map((hour) => parseInt(hour, 10)) // Convert to numbers for sorting
    .sort((a, b) => a - b) // Sort numerically
    .map((hour) => String(hour).padStart(2, "0")); // Convert back to padded strings

  return (
    <ItemListPopup title="Playlist details" onClose={onClose}>
      <div className="flex flex-col gap-6 h-full overflow-y-auto">
        {/* Dimensions */}
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">Dimension</span>
          <span className="text-sm text-gray-500">{getDimensions()}</span>
        </div>

        {/* Image Preview */}
        <div className="flex justify-center">
          <div className="w-full flex justify-center">
            <img
              src={creative.imageUrl}
              alt={creative.title || "Creative Preview"}
              className={`h-auto rounded-lg border-4 border-[#A6A6A6] ${
                creative.orientation === "portrait" ? "w-1/2" : "w-full"
              }`}
            />
          </div>
        </div>

        {/* Creative Name */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">
            Creative name
          </div>
          <div className="text-base font-semibold text-gray-900">
            {creative.title || "Untitled Creative"}
          </div>
        </div>

        <div className="relative w-full flex items-center">
          {/* Schedule aligned to start */}
          <div className="text-left">
            <div className="text-sm font-medium text-gray-700">Schedule:</div>
            <div className="text-base font-semibold text-gray-900">
              {getScheduleTypeText()}
            </div>
          </div>

          {/* Duration centered absolutely */}
          <div className="absolute left-1/2 transform -translate-x-1/2 text-center">
            <div className="text-sm font-medium text-gray-700">Duration:</div>
            <div className="text-base font-semibold text-gray-900">
              {getDurationText()}
            </div>
          </div>
        </div>

        {/* Announcement Type */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">
            Announcement type:
          </div>
          <AnnouncementTypeCard
            type={creative.announcement}
            isSelected={true}
            showDescription={false}
          />
        </div>

        {/* Custom Schedule Calendar (only for selection type) */}
        {creative.scheduleType === "selection" && (
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              Calendar selection
            </div>
            <div className="bg-white text-black p-2 rounded-lg">
              <div className="grid grid-cols-[48px_repeat(7,1fr)]">
                <div></div>
                {days.map((day) => (
                  <div
                    key={day}
                    className="text-xs font-semibold text-center py-1"
                  >
                    {day}
                  </div>
                ))}
                {selectedHours.map((hour) => (
                  <React.Fragment key={hour}>
                    <div className="text-xs font-medium text-center py-1">
                      {hour}
                    </div>
                    {days.map((day) => {
                      const key = `${day}-${hour}`;
                      return (
                        <div
                          key={key}
                          className={`w-full h-[22px] rounded-[4px] cursor-default ${getButtonClass(
                            key
                          )}`}
                        />
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Start & End Date (for non-selection types) */}
        {creative.scheduleType !== "selection" && (
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">
              Start & end date
            </div>
            <div className="text-base font-semibold text-gray-900">
              {getDateRange()}
            </div>
          </div>
        )}

        {/* Selected Tags (only for tag-level announcement) */}
        {creative.announcement === "tag-level" && (
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              Selected ({getSelectedTagsCount()})
            </div>
            <div className="flex flex-wrap gap-2">
              {tagsToDisplay.length > 0 ? (
                tagsToDisplay.map((tag: string, index: number) => (
                  <div
                    key={index}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm font-medium border"
                  >
                    {tag}
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 italic">
                  No tags selected
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </ItemListPopup>
  );
};

export default ScheduledCreativePreview;
